import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ReviewCountdown } from '../Community/marketProfile/MarketProfileShared';
import {
  communityPathForStatus,
  fetchCommunityMembershipStatus,
  type CommunityMembershipState,
} from '../../services/communityMembershipService';
import '../Community/CommunityForum.css';

const POLL_MS = 15_000;

export default function CommunityApplicationPendingPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<CommunityMembershipState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;

    let cancelled = false;

    const load = async () => {
      const next = await fetchCommunityMembershipStatus();
      if (cancelled) return;
      if (!next) {
        setError(true);
        setLoading(false);
        return;
      }

      if (next.status === 'approved') {
        navigate(communityPathForStatus('approved'), { replace: true });
        return;
      }
      if (next.status === 'none' || next.status === 'rejected') {
        navigate('/private-circle#application', { replace: true });
        return;
      }

      setState(next);
      setLoading(false);
      setError(false);
    };

    load();
    const id = window.setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [authLoading, navigate, user]);

  if (authLoading || loading) {
    return (
      <div className="dcf-page dcf-page--loading" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p className="text-sm text-slate-500">Загрузка…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dcf-auth-gate">
        <h1 className="text-lg font-semibold text-slate-900">Сообщество datacircle</h1>
        <p className="mt-3 text-sm text-slate-600">Войдите в аккаунт, чтобы проверить статус заявки.</p>
        <Link to="/" className="dcf-btn dcf-btn--primary mt-6 inline-flex">
          На главную
        </Link>
      </div>
    );
  }

  if (error || !state?.submittedAt) {
    return (
      <div className="dcf-auth-gate">
        <h1 className="text-lg font-semibold text-slate-900">Не удалось загрузить статус</h1>
        <p className="mt-3 text-sm text-slate-600">Попробуйте обновить страницу.</p>
        <button type="button" className="dcf-btn dcf-btn--primary mt-6" onClick={() => window.location.reload()}>
          Обновить
        </button>
      </div>
    );
  }

  return (
    <div className="dcf-mp-form dcf-mp-review-pending">
      <Link to="/" className="dcf-btn dcf-btn--ghost dcf-mp-back">
        ← На главную
      </Link>

      <section className="dcf-mp-card dcf-mp-review-pending__card">
        <div className="dcf-mp-review-pending__icon" aria-hidden="true">
          <i className="fas fa-user-check" />
        </div>
        <h1 className="dcf-mp-review-pending__title">Заявка на рассмотрении</h1>
        <p className="dcf-mp-review-pending__text">
          Заявка на вступление в сообщество datacircle находится на рассмотрении. Наши модераторы уже проверяют данные и в
          ближайшее время свяжутся с Вами для уточнения информации. Обычно это происходит в
          течение одного дня.
        </p>

        <div className="dcf-mp-review-pending__status">
          <span className="dcf-status-pill dcf-mp-pill--review">
            <i className="fas fa-hourglass-half" aria-hidden="true" />
            На проверке
          </span>
        </div>

        <ReviewCountdown reviewSubmittedAt={state.submittedAt} showLabel={false} />

        <p className="dcf-mp-review-pending__hint">
          Когда заявка будет одобрена, Вы сможете авторизоваться в сообщество. Эту страницу можно закрыть —
          статус будет обновлен автоматически.
        </p>

        <div className="dcf-mp-form-actions">
          <Link to="/" className="dcf-btn dcf-btn--secondary">
            На главную
          </Link>
          <Link to="/private-circle" className="dcf-btn dcf-btn--ghost">
            О сообществе
          </Link>
        </div>
      </section>
    </div>
  );
}
